import { useState } from 'react';
import PropTypes from 'prop-types';
import { Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { createPageUrl } from '@/utils';
import { fmtCurrency } from '@/utils/money';
import { milesGold } from './milesCardStyles';

/** One attempt key per trip, kept for the tab: a retry or double tap finds the booking it already made.
 *  A trip with no id gets a fresh key every time, so two such trips never share one. */
function attemptKey(userId, flightId) {
  if (!flightId) return crypto.randomUUID();
  const storageKey = `defera_miles_only_idem:${userId}:${flightId}`;
  try {
    const existing = sessionStorage.getItem(storageKey);
    if (existing) return existing;
    const fresh = crypto.randomUUID();
    sessionStorage.setItem(storageKey, fresh);
    return fresh;
  } catch {
    return crypto.randomUUID();
  }
}

/**
 * Booking a trip the customer's Defera Miles pay for entirely: no card, no
 * plan. Takes the place of the card form on Review. The server prices the
 * trip itself, holds the Miles and writes the booking
 * (server/miles-only-booking.js via POST /api/admin/bookings); this sends the
 * trip and reports back.
 */
export default function MilesOnlyCheckout({
  flightData, passengers, cabinClass, rail, miles, tripTotalCents, sessionId, disabled, onMilesRefused,
}) {
  const [submitting, setSubmitting] = useState(false);

  const book = async () => {
    if (submitting || disabled) return;
    setSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error('Please sign in again to book with your Miles.');
        return;
      }
      const payload = {
        user_id: session.user.id,
        offer_id: flightData?.id || flightData?.offer_id,
        status: 'confirmed',
        total_currency: 'USD',
        passenger_count: Array.isArray(passengers) ? passengers.length : 1,
        cabin_class: cabinClass || null,
        flight_data: {
          ...flightData,
          payment_method: 'miles',
          _payment_method: 'miles',
          booking_option: 'miles',
          miles_rail: rail,
        },
        passengers: passengers || [],
        client_request_id: attemptKey(session.user.id, flightData?.id || flightData?.offer_id),
        ...(sessionId ? { session_id: sessionId } : {}),
      };
      const res = await fetch('/api/admin/bookings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify(payload),
      });
      let body = null;
      try { body = await res.json(); } catch { /* handled below */ }
      if (res.ok && body?.data?.id) {
        window.location.href = createPageUrl('Dashboard') + '?miles_booking=success';
        return;
      }
      if (body?.code === 'miles_unavailable') {
        toast.error(body.error || 'Your Miles balance changed since this page loaded. Please refresh and try again.');
        onMilesRefused?.(body);
        return;
      }
      toast.error(body?.error || 'We couldn’t book this trip just now. Please try again in a moment.');
    } catch {
      toast.error('We couldn’t book this trip just now. Please try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto text-center" data-testid="miles-only-checkout">
      <p className="text-white text-2xl font-semibold tracking-tight">$0 due today</p>
      <p className="mt-1.5 text-sm font-normal text-[#f3dca6]">Your Miles cover this whole trip</p>
      <p className="mt-1 text-[13px] font-normal text-white/55 max-w-[22rem] mx-auto leading-snug">
        {miles.toLocaleString()} Defera Miles pay the ${fmtCurrency(tripTotalCents)} trip total. Nothing is due later, and no card is needed.
      </p>
      <button
        type="button"
        onClick={book}
        disabled={disabled || submitting}
        className={`mt-6 w-full h-14 rounded-full text-[15px] font-semibold inline-flex items-center justify-center gap-2 ${milesGold.button}`}
      >
        {submitting
          ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
          : <Sparkles className="w-4 h-4" aria-hidden="true" />}
        {submitting ? 'Booking…' : `Book with ${miles.toLocaleString()} Miles`}
      </button>
      <p className="mt-3 text-[12px] font-normal text-white/45">
        If the trip is cancelled, your Miles come back to your account.
      </p>
    </div>
  );
}

MilesOnlyCheckout.propTypes = {
  /** The checkout's flight_data (buildFlightDataForApi), carrying the Miles claim. */
  flightData: PropTypes.object.isRequired,
  passengers: PropTypes.array,
  cabinClass: PropTypes.string,
  /** The rail the trip was priced on: 'defera_capital' or 'defera_zero'. */
  rail: PropTypes.string.isRequired,
  miles: PropTypes.number.isRequired,
  tripTotalCents: PropTypes.number.isRequired,
  sessionId: PropTypes.string,
  disabled: PropTypes.bool,
  onMilesRefused: PropTypes.func,
};
